import { Navigate, useLocation } from "react-router-dom";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { practisesAnyProfession } from "@/lib/is-professional";

type TenantRole = "owner" | "admin" | "member" | "read_only";
type PlatformRole = "root" | "root_admin";
/**
 * `"professional"` means ANY profession, and it is the one a route should
 * normally use.
 *
 * `"notary"` and `"accountant"` remain for routes that genuinely concern one
 * profession. They were used for routes that concern ALL of them — /earnings,
 * /earnings/request-payout and /security/two-factor — which bounced a third
 * profession away from its own earnings and its own payout request.
 */
type AllowedRole =
  | TenantRole
  | PlatformRole
  | "notary"
  | "accountant"
  | "professional";

interface ProtectedRouteProps {
  children: JSX.Element;
  allowedRoles?: AllowedRole[];
}

export function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isVerifyEmailPage = location.pathname === "/verify-email";
  const isSetInitialPasswordPage = location.pathname === "/onboarding/set-password";
  const isNameEntryPage = location.pathname === "/auth/onboarding/name";

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user != null && user.emailVerifiedAt == null && !isVerifyEmailPage) {
    const returnTo = location.pathname + location.search;
    const target =
      returnTo && returnTo !== "/" && returnTo !== "/verify-email"
        ? `/verify-email?returnTo=${encodeURIComponent(returnTo)}`
        : "/verify-email";
    return <Navigate to={target} replace />;
  }

  // Root users without a password (typically right after accepting an invite)
  // are forced to set one before they can do anything else. They can't fall
  // back to magic link or Google SSO for platform accounts.
  const isRootUser =
    user != null &&
    (user.platformRole === "root" || user.platformRole === "root_admin");
  if (isRootUser && user.hasPassword === false && !isSetInitialPasswordPage) {
    return <Navigate to="/onboarding/set-password" replace />;
  }

  // Consumer onboarding gates. The account-type picker has been retired —
  // every new consumer is registered as a `person` (see auth.service
  // register() and sso.service handleCallback). Existing users with a null
  // `consumerAccountKind` fall through here without being bounced; they
  // can browse and create requests normally. The name gate stays — we
  // still need first/last name to issue invoices and personalise the UI.
  const isConsumer =
    user != null &&
    !isRootUser &&
    user.isNotary !== true &&
    user.isAccountant !== true &&
    !user.role;
  if (isConsumer && user != null) {
    const needsName =
      (user.firstName?.trim() ?? "") === "" ||
      (user.lastName?.trim() ?? "") === "";
    if (needsName && !isNameEntryPage) {
      return <Navigate to="/auth/onboarding/name" replace />;
    }
  }

  if (allowedRoles != null && allowedRoles.length > 0 && user != null) {
    const hasRole = allowedRoles.some(
      (r) =>
        r === user.role ||
        r === user.platformRole ||
        (r === "professional" && practisesAnyProfession(user)) ||
        (r === "notary" && user.isNotary === true) ||
        (r === "accountant" && (user.role === "accountant" || user.isAccountant === true))
    );
    if (!hasRole) {
      /*
        WHERE A REFUSED PROFESSIONAL LANDS. This read `user.isNotary`, so a
        third profession fell past it to the consumer dashboard — a page that
        is not theirs. Any professional goes to /requests, which is the
        professional home for all of them.
      */
      if (practisesAnyProfession(user)) {
        return <Navigate to="/requests" replace />;
      }
      if (user.platformRole === "root" || user.platformRole === "root_admin") {
        return <Navigate to="/tenants" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
